// Lo que la web lee de /api/oficina/pelotas: espejo a mano de backend/src/oficina/pelotas/dto.ts (que importa de
// repo.ts y por eso no entra en el generador de compartido/). Si cambia el DTO, cambia aquí en el mismo commit.
// 03-11 (ventana «Santi» nueva, docs/pelotas/ux-2026-11-03/construccion.md): la lista ya no lleva `atascado`,
// `abiertos`, `tuyos` ni `atascados`; trae lo nuevo de Santi (`nuevos`), el último mensaje de una persona, lo cerrado
// esta semana, el resumen de la pared y la fila «Chat con Santi».

/** El último mensaje de una PERSONA (nunca una nota técnica entre agentes). */
export interface UltimoDTO {
  de: string;
  texto: string;
  en: string;
}

export interface ContratoDTO {
  temaId: number;
  titulo: string;
  proyecto: string;
  /** Quién lo pidió (persona del centro: `pau`, `santi`…). */
  pedidoPor: string;
  /** Cuándo se pidió (ISO; `creado_en` del centro). */
  pedidoEn: string;
  /** A quién le toca (persona del centro) y desde cuándo (ISO). */
  lado: string;
  desde: string;
  /** Fecha límite (`AAAA-MM-DD`) o null. */
  vence: string | null;
  queFalta: string;
  /**
   * El agente de este contrato (entrega 3: el que lo escaló o el que le puso la oficina al abrirlo), o null si aún no
   * tiene uno vivo (al abrirlo, la web pide uno: `POST /temas/:id/agente`).
   */
  agentId: string | null;
  ultimo: UltimoDTO | null;
  /** El último movimiento o mensaje (ISO): el orden de la lista. */
  ultimoEn: string;
  /** Cosas de Santi desde la última vez que Pau lo vio (el punto blanco y la raya «Nuevo»). */
  nuevos: number;
  /**
   * Hay un borrador de su agente esperando en la caja de este contrato (la fila dice «Borrador ·»). Opcional en el
   * espejo: un servidor de antes de la entrega 2 no lo manda (= no hay).
   */
  borrador?: boolean;
  /** Entrega 3: su agente tiene una «Propuesta para Santi» en su chat (la fila dice «Propuesta ·»). */
  propuesta?: boolean;
  /** Lo había hasta la entrega 2 (la tarjeta «Para Santi»); sin tarjetas, siempre false. Lo lee aún la lista del equipo. */
  esperaTuSi?: boolean;
}

/**
 * Un borrador del agente de Santi (entrega 2, construccion.md §«Entrega 2»; backend dto.ts `BorradorDTO`): uno vigente
 * por hilo. Lo deja el agente (al pedírselo Pau con «Que lo prepare tu agente» / «Otra versión», o solo cuando Santi
 * escribe en algo que le toca a Pau); `porque` es la frase llana que deja enviarlo sin leer nada técnico; `en` lo
 * identifica (cada versión, la suya). `cerrar`: el agente lo daría por hecho y `texto` es la nota (Hecho sigue siendo de Pau).
 */
export interface BorradorDTO {
  tipo: 'mensaje' | 'cerrar';
  texto: string;
  porque: string;
  agentId: string;
  en: string;
}

/** El «Pedir algo a Santi» que preparó un agente (`pelota_abrir`): lo abre «Revisar» en su chat. */
export interface BorradorPedidoDTO {
  titulo: string;
  texto: string;
  fechaLimite: string | null;
  porque: string;
  agentId: string;
  en: string;
}

/** `GET /agentes/:id`: lo que la web pinta en el chat de un agente (su pedido preparado, o null). */
export interface AgenteSantiDTO {
  pedido: BorradorPedidoDTO | null;
}

export type MotivoCierre = 'hecho' | 'cancelado';

export interface CerradoDTO {
  temaId: number;
  titulo: string;
  motivo: MotivoCierre;
  cerradoEn: string;
  /** Quién lo cerró (persona del centro). */
  por: string;
}

/** La pantalla «Santi» de la pared: un solo estado a la vez (venció › vence hoy › nuevos › nada). */
export interface ParedDTO {
  /** Contratos (y el chat) con algo de Santi sin ver: la misma cifra que los puntos blancos de la lista. */
  nuevos: number;
  /** Dónde está lo más reciente de Santi sin ver (el tema, o el chat). */
  masReciente: number | 'general' | null;
  /** El contrato que vence hoy (su tema), si hay. */
  venceHoy: number | null;
  /** El contrato que ya venció (su tema), si hay. */
  vencido: number | null;
}

export interface ListaContratosDTO {
  configurado: boolean;
  /** Abiertos, por `ultimoEn` (lo más reciente arriba). */
  contratos: ContratoDTO[];
  /** Cerrados o cancelados en los últimos 7 días, por `cerradoEn`. */
  cerradosSemana: CerradoDTO[];
  /** La fila «Chat con Santi» (`borrador`: hay uno esperando en su caja; opcional para un servidor de antes). */
  general: { ultimo: UltimoDTO | null; nuevos: number; borrador?: boolean; propuesta?: boolean };
  pared: ParedDTO;
  servidoEn: string;
  centroCaido: boolean;
}

export interface MensajeHiloDTO {
  seq: number;
  en: string;
  de: string;
  porAgente: boolean;
  agente: string | null;
  tipo: 'abierto' | 'pase' | 'cerrado' | 'mensaje' | 'nota_tecnica';
  texto: string;
  /** Un pase que fue con un mensaje: el seq de ese mensaje (el mensaje ya es su globo; el pase no se pinta). */
  mensajeSeq?: number | null;
  /** En el cierre: hecho o cancelado. */
  motivo?: MotivoCierre;
}

export interface HiloDTO {
  contrato: ContratoDTO;
  estado: 'abierto' | 'hecho';
  pedidoPor: string;
  detalle: string;
  mensajes: MensajeHiloDTO[];
  /** Entrega 2 (ya no entra solo en la caja): un servidor de antes de la entrega 3 manda la propuesta con este nombre. */
  borrador?: BorradorDTO | null;
  /** Entrega 3: la «Propuesta para Santi» de su agente (en SU chat, con «Usar en la caja»), o null. */
  propuesta?: PropuestaDTO | null;
}

export interface HiloGeneralDTO {
  mensajes: MensajeHiloDTO[];
  /** El agente del chat con Santi (el de General), o null si aún no tiene. */
  agentId: string | null;
  /** Como en HiloDTO: `propuesta` (entrega 3) o, de un servidor de antes, `borrador`. */
  borrador?: BorradorDTO | null;
  propuesta?: PropuestaDTO | null;
}

/**
 * Entrega 3 (construccion.md §«Entrega 3»): lo que propone el agente de un hilo para Santi. Los mismos campos que el
 * borrador de la entrega 2, pero ya no entra solo en la caja: sale en el chat del agente como «Propuesta para Santi» y
 * solo pasa a la caja con «Usar en la caja» (`POST /temas/:hilo/propuesta/usar`).
 */
export interface PropuestaDTO extends BorradorDTO {
  /** El mensaje «Propuesta para Santi» que la oficina dejó en el chat del agente (la tarjeta vigente); null en las de antes. */
  mensajeId?: string | null;
  /** Cuándo la pasó Pau a la caja; null = aún no (la tarjeta ofrece «Usar en la caja»). */
  usadaEn?: string | null;
}

/** La propuesta de un hilo, venga con el nombre de la entrega 3 o con el de la 2. */
export function propuestaDe(h: { propuesta?: PropuestaDTO | null; borrador?: BorradorDTO | null } | null | undefined): PropuestaDTO | null {
  return h?.propuesta ?? h?.borrador ?? null;
}

/** La que aún se puede usar (sin `usadaEn`): la que enseña la tarjeta del chat del agente. */
export function propuestaVigente(h: Parameters<typeof propuestaDe>[0]): PropuestaDTO | null {
  const p = propuestaDe(h);
  return p && !p.usadaEn ? p : null;
}

/** Qué enseña la columna 2 de la ventana «Santi»: el chat, un contrato o «Pedir algo a Santi». */
export type HiloAbierto = 'general' | 'pedir' | number;
